/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import {
  DateStepper, SegmentedPills, StatusPill,
  Sparkline, SlideOverPanel, SecondaryBtn,
  rangeForPeriod, priorPeriodRange,
} from './components/systemKit';
import DismissAlertModal from './components/DismissAlertModal';
import PremiumLineChart, { fmtDateShort } from './components/PremiumLineChart';
import { isItemUsable } from './lib/parseGuardrails';
import { generateInsight } from './lib/inventoryInsight';
import { navigate } from './lib/navigate';

// Inventory Trends — the deep-dive Volume + case-size analysis page.
// Sits alongside Price Tracker in Operations. Shares the three-concepts architecture
// from DESIGN_SYSTEM.md: Volume + Spend + catch-weight-aware price are all surfaced
// here, but the primary lens is Volume (quantity / weight ordered over time).

const CATEGORIES = ['All', 'Proteins', 'Produce', 'Dairy', 'Dry Goods', 'Beverages', 'Seafood', 'Desserts', 'Other'];

// hasWeightData — same rule as FoodPriceTracker, duplicated here to keep this page
// self-contained. Consolidate into a util module in a future cleanup.
function hasWeightData(item, lines) {
  if (item?.is_catch_weight) return true;
  if (item?.weight_per_case != null) return true;
  if (lines?.some?.(l => l.total_weight != null)) return true;
  return false;
}

function fmtDelta(pct) {
  if (pct == null || !isFinite(pct)) return '—';
  const sign = pct >= 0 ? '↑' : '↓';
  return `${sign} ${Math.abs(pct).toFixed(0)}%`;
}

// MetricStrip — single-row totals for the Inventory Trends page header area.
// Replaced the 4-card KPI grid (Total Spend / Total Lbs / Top Growing / Top
// Shrinking) when the segmentation commit demoted Top Growing/Shrinking — the
// items table's Active group sort already surfaces them as top rows.
//
// Page-local. Not promoted to systemKit because the 4-metric + 1-action shape
// is too Inventory-specific to generalize. If a similar pattern appears
// elsewhere with two real consumers, refactor then. See DESIGN_SYSTEM.md
// §Compact metric strip.
function MetricStrip({ hasCurrentInvoices, totalSpend, totalLbs, alertsCount, needsReviewCount, onOpenReport, onGoToAlerts, onGoToNeedsReview }) {
  const fmtMoneyLocal = (n) => {
    if (n == null || !isFinite(n)) return '$0';
    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return `$${Math.round(n).toLocaleString('en-US')}`;
  };
  const Sep = () => (
    <span aria-hidden style={{ color: 'var(--text-muted)' }}>{'\u00B7'}</span>
  );
  const Metric = ({ value, label }) => (
    <span style={{ fontSize: 13 }}>
      <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>{label}</span>
    </span>
  );
  const MetricLink = ({ onClick, children }) => (
    <button
      onClick={onClick}
      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: 'var(--text-warning)', fontWeight: 500 }}
      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
    >
      {children}
    </button>
  );

  return (
    <div
      className="rounded-lg flex items-center justify-between gap-3 flex-wrap mb-4"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', padding: '12px 16px' }}
    >
      <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: 13 }}>
        {!hasCurrentInvoices ? (
          <span style={{ color: 'var(--text-muted)' }}>No purchases this period</span>
        ) : (
          <>
            <Metric value={fmtMoneyLocal(totalSpend)} label="spend" />
            <Sep />
            <Metric value={`${Math.round(totalLbs || 0).toLocaleString()}`} label="lbs" />
            {alertsCount > 0 && (
              <>
                <Sep />
                <MetricLink onClick={onGoToAlerts}>{`${alertsCount} ${alertsCount === 1 ? 'alert' : 'alerts'} active`}</MetricLink>
              </>
            )}
            {needsReviewCount > 0 && (
              <>
                <Sep />
                <MetricLink onClick={onGoToNeedsReview}>{`${needsReviewCount} needs review`}</MetricLink>
              </>
            )}
          </>
        )}
      </div>
      <button
        onClick={onOpenReport}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: 'var(--accent-primary)', fontWeight: 500 }}
        onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
        onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
      >
        {`Open invoices \u2192`}
      </button>
    </div>
  );
}

// widerPeriod — used by the empty-period "try a wider range" inline action.
// Returns the next-larger calendar granularity, or null when already at year.
function widerPeriod(p) {
  return ({ week: 'month', month: 'quarter', quarter: 'year', year: null })[p] || null;
}
function periodLabel(p) {
  return ({ week: 'Week', month: 'Month', quarter: 'Quarter', year: 'Year' })[p] || p;
}

export default function InventoryTrends({ companyId }) {
  // Calendar-based period state. Default: current calendar month. See
  // DESIGN_SYSTEM.md §Period-aware DateStepper.
  const [period, setPeriod] = useState('month');
  const [range, setRange] = useState(() => rangeForPeriod('month', new Date()));
  const [category, setCategory] = useState('All');
  const [vendorFilter, setVendorFilter] = useState(''); // blank = all vendors

  // Data
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // canonical food_items
  const [lines, setLines] = useState([]); // invoice_line_items for the current + prior range
  const [vendors, setVendors] = useState([]); // distinct vendor_name strings
  const [drawer, setDrawer] = useState(null); // selected canonical item
  const [alerts, setAlerts] = useState([]);   // active shrinkflation_alerts for inline chips + drawer card

  // Prior range = calendar prior period (prior week, prior month, etc.).
  // Driven by the current `period` so the comparison stays semantically aligned
  // with the user's mental model.
  const priorRange = useMemo(() => priorPeriodRange(period, range), [period, range]);

  // Fetch items + lines for the combined window (prior + current).
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [itemsRes, linesRes] = await Promise.all([
        supabase.from('food_items').select('*').eq('company_id', companyId),
        supabase.from('invoice_line_items')
          .select('food_item_id, invoice_date, vendor_name, quantity, unit, total_weight, price_per_lb, unit_price, line_total')
          .eq('company_id', companyId)
          .gte('invoice_date', priorRange.start)
          .lte('invoice_date', range.end),
      ]);
      if (cancelled) return;
      setItems(itemsRes.data || []);
      setLines(linesRes.data || []);
      const uniq = Array.from(new Set((linesRes.data || []).map(l => l.vendor_name).filter(Boolean)));
      setVendors(uniq);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [companyId, range.start, range.end, priorRange.start, priorRange.end]);

  // Fetch active shrinkflation_alerts. Drives the inline chip on items table rows
  // and the alert card at the top of the drawer.
  const fetchAlerts = React.useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('shrinkflation_alerts')
      .select('*')
      .eq('company_id', companyId)
      .eq('status', 'active');
    setAlerts(data || []);
  }, [companyId]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const activeAlertByItemId = useMemo(() => {
    const m = {};
    (alerts || []).forEach(a => { m[a.food_item_id] = a; });
    return m;
  }, [alerts]);

  // Per-item roll-up. Computed from `lines` + `items`. Flagged items
  // (needs_review = TRUE) are passed through to the table for display but
  // contribute 0 to aggregates — see DESIGN_SYSTEM.md §Data Review queue.
  const perItem = useMemo(() => {
    const byId = {};
    for (const it of items) {
      byId[it.id] = {
        id: it.id, name: it.normalized_name, category: it.category,
        is_catch_weight: it.is_catch_weight, weight_per_case: it.weight_per_case,
        needs_review: !!it.needs_review,
        review_reason: it.review_reason || null,
        linesCurr: [], linesPrior: [],
      };
    }
    for (const li of lines) {
      if (!byId[li.food_item_id]) continue;
      if (vendorFilter && li.vendor_name !== vendorFilter) continue;
      const bucket = li.invoice_date >= range.start && li.invoice_date <= range.end ? 'linesCurr' : 'linesPrior';
      byId[li.food_item_id][bucket].push(li);
    }
    const rows = Object.values(byId).map(row => {
      const allLines = [...row.linesCurr, ...row.linesPrior];
      const useWeight = hasWeightData(row, allLines);
      const curr = row.linesCurr.reduce((acc, l) => {
        acc.qty += Number(l.quantity) || 0;
        acc.lbs += Number(l.total_weight) || 0;
        acc.spend += Number(l.line_total) || 0;
        if (l.price_per_lb != null) { acc.perLbSum += Number(l.price_per_lb); acc.perLbCount += 1; }
        return acc;
      }, { qty: 0, lbs: 0, spend: 0, perLbSum: 0, perLbCount: 0 });
      const prior = row.linesPrior.reduce((acc, l) => {
        acc.qty += Number(l.quantity) || 0;
        acc.lbs += Number(l.total_weight) || 0;
        acc.spend += Number(l.line_total) || 0;
        if (l.price_per_lb != null) { acc.perLbSum += Number(l.price_per_lb); acc.perLbCount += 1; }
        return acc;
      }, { qty: 0, lbs: 0, spend: 0, perLbSum: 0, perLbCount: 0 });
      const currVol = useWeight ? curr.lbs : curr.qty;
      const priorVol = useWeight ? prior.lbs : prior.qty;
      const volPct = priorVol > 0 ? ((currVol - priorVol) / priorVol) * 100 : null;
      const currPerLb = curr.perLbCount > 0 ? curr.perLbSum / curr.perLbCount : null;
      const priorPerLb = prior.perLbCount > 0 ? prior.perLbSum / prior.perLbCount : null;
      const perLbPct = (priorPerLb != null && currPerLb != null && priorPerLb > 0) ? ((currPerLb - priorPerLb) / priorPerLb) * 100 : null;
      const sparkline = row.linesCurr
        .slice()
        .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date))
        .map(l => useWeight ? (Number(l.total_weight) || 0) : (Number(l.quantity) || 0))
        .filter(v => v > 0);
      return {
        ...row,
        useWeight,
        curr, prior,
        currVol, priorVol, volPct,
        currPerLb, priorPerLb, perLbPct,
        sparkline,
      };
    });
    return rows;
  }, [items, lines, range.start, range.end, vendorFilter]);

  // Activity-status segmentation. See DESIGN_SYSTEM.md §Activity-status segmentation.
  // Flat "show every item with any activity" tables drown the operator in red
  // -100% rows when current periods are sparse. Splitting into Active +
  // Skipped lets the page describe what's actually happening: things you're
  // ordering vs things you've stopped ordering this period.
  //
  // Filters (category + vendor) apply to BOTH groups identically.
  // Both-zero rows (catalog items with no activity in either period) are
  // dropped — they don't belong on a trend page.
  const segmented = useMemo(() => {
    let pool = perItem;
    if (category !== 'All') pool = pool.filter(r => r.category === category);
    // Vendor filter: applied earlier in `perItem` via line-level filter when
    // computing roll-ups, so no extra filter step here.

    const active = [];
    const skipped = [];
    for (const r of pool) {
      if (r.currVol > 0) {
        active.push(r);
      } else if (r.priorVol > 0) {
        skipped.push(r);
      }
      // currVol=0 AND priorVol=0 → dropped intentionally.
    }
    // Active sort: largest absolute movement first — the items that
    // actually changed lead the table. Items with no prior comparison
    // (volPct == null, "New") get sorted to the end of Active by |currVol|.
    active.sort((a, b) => {
      const aMag = a.volPct != null ? Math.abs(a.volPct) : -Infinity;
      const bMag = b.volPct != null ? Math.abs(b.volPct) : -Infinity;
      if (aMag === bMag) return b.currVol - a.currVol;
      return bMag - aMag;
    });
    // Skipped sort: largest prior volume first — most-impactful skips lead.
    skipped.sort((a, b) => b.priorVol - a.priorVol);

    return { active, skipped };
  }, [perItem, category]);

  // Empty-period detection — distinguishes "filter zeroed everything" from
  // "the user picked a window with no purchases in it." Different copy.
  const hasCurrentInvoices = useMemo(
    () => lines.some(l => l.invoice_date >= range.start && l.invoice_date <= range.end),
    [lines, range.start, range.end]
  );
  // hasPriorInvoices — when the prior window is entirely empty (e.g. a newly
  // onboarded company where this is the first month of invoice history), the
  // per-row "New" chip becomes pure noise — it fires on EVERY item. Detect
  // that case so we can suppress the chips and show a single page-level
  // banner instead. See DESIGN_SYSTEM.md §Activity-status segmentation
  // (first-period banner).
  const hasPriorInvoices = useMemo(
    () => lines.some(l => l.invoice_date >= priorRange.start && l.invoice_date <= priorRange.end),
    [lines, priorRange.start, priorRange.end]
  );
  const isFirstPeriod = hasCurrentInvoices && !hasPriorInvoices;

  // KPI cards. Flagged items (needs_review) excluded from aggregates per
  // DESIGN_SYSTEM.md §Data Review queue.
  //
  // topGrow / topShrink: REQUIRE both currVol>0 AND priorVol>0. New items
  // (priorVol=0, volPct=null) and Skipped items (currVol=0) have no comparable
  // % so they don't belong on the leaderboard. Pre-fix, an empty current week
  // would surface a -100% Skipped item as "Top growing" — literally the
  // opposite of growth.
  const totals = useMemo(() => {
    let spend = 0, lbs = 0, topGrow = null, topShrink = null;
    for (const r of perItem) {
      if (!isItemUsable(r)) continue;
      spend += r.curr.spend;
      lbs += r.curr.lbs;
      const comparable = r.currVol > 0 && r.priorVol > 0 && r.volPct != null;
      if (!comparable) continue;
      if (r.volPct > 0 && (!topGrow || r.volPct > topGrow.volPct)) topGrow = r;
      if (r.volPct < 0 && (!topShrink || r.volPct < topShrink.volPct)) topShrink = r;
    }
    return { spend, lbs, topGrow, topShrink };
  }, [perItem]);

  // Top-categories summary line input. Flagged items excluded.
  const catRollup = useMemo(() => {
    const buckets = {};
    for (const r of perItem) {
      if (!isItemUsable(r)) continue;
      const cat = r.category || 'Uncategorized';
      if (!buckets[cat]) buckets[cat] = { category: cat, curr: 0, prior: 0 };
      buckets[cat].curr += r.useWeight ? r.curr.lbs : r.curr.qty;
      buckets[cat].prior += r.useWeight ? r.prior.lbs : r.prior.qty;
    }
    return Object.values(buckets).filter(b => b.curr > 0).sort((a, b) => b.curr - a.curr);
  }, [perItem]);

  // Inline action: bump the period selector to the next-wider granularity
  // and re-anchor on today. Used by the empty-period "try a wider range"
  // copy when the current window has zero invoices.
  const handleWiderRange = () => {
    const wider = widerPeriod(period);
    if (!wider) return;
    setPeriod(wider);
    setRange(rangeForPeriod(wider, new Date()));
  };
  const handleClearFilters = () => {
    setCategory('All');
    setVendorFilter('');
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Inventory Trends</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Volume and case-size analysis across your items</p>
          </div>
          <DateStepper
            period={period}
            onPeriodChange={setPeriod}
            range={range}
            onRangeChange={setRange}
            alignment="right"
          />
        </div>

        {/* Category + Vendor filters */}
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <SegmentedPills
            value={category}
            onChange={setCategory}
            options={CATEGORIES.map(c => ({ key: c, label: c }))}
            size="sm"
          />
          <details className="relative">
            <summary
              className="cursor-pointer text-sm rounded-lg px-3 py-1.5"
              style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', listStyle: 'none' }}
            >
              Vendor: {vendorFilter || 'All'} ▾
            </summary>
            <div
              className="absolute z-10 mt-1 min-w-[200px] p-2 rounded-lg"
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
            >
              <button
                onClick={() => setVendorFilter('')}
                className="block w-full text-left px-2 py-1 text-sm rounded"
                style={{ color: vendorFilter === '' ? 'var(--accent-primary)' : 'var(--text-primary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                All vendors
              </button>
              {vendors.map(v => (
                <button
                  key={v}
                  onClick={() => setVendorFilter(v)}
                  className="block w-full text-left px-2 py-1 text-sm rounded"
                  style={{ color: vendorFilter === v ? 'var(--accent-primary)' : 'var(--text-primary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  {v.replace(/\n/g, ' · ')}
                </button>
              ))}
            </div>
          </details>
        </div>

        {/* Compact MetricStrip replaces the 4-card KPI grid. Top Growing /
            Top Shrinking demoted to the items table's Active sort (which
            already surfaces them as top rows). See DESIGN_SYSTEM.md §Compact
            metric strip. */}
        <MetricStrip
          hasCurrentInvoices={hasCurrentInvoices}
          totalSpend={totals.spend}
          totalLbs={totals.lbs}
          alertsCount={alerts.length}
          needsReviewCount={items.filter(it => it.needs_review).length}
          onOpenReport={() => navigate('inventory', 'invoices', { period, anchor: range.start })}
          onGoToAlerts={() => {
            navigate('inventory', 'pricetracker');
            // Defer until Price Tracker mounts + fetches. 300ms matches the
            // pattern used in VolumeDetailDrawer's section-scroll handlers.
            setTimeout(() => document.querySelector('[data-vendor-watch-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
          }}
          onGoToNeedsReview={() => {
            navigate('inventory', 'pricetracker');
            setTimeout(() => document.querySelector('[data-data-review-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
          }}
        />

        {/* Top categories — single summary line. Replaces a former bar chart that
            promised "current vs prior" but only delivered current (most items lack
            prior-period data) and was dominated by parser-error outliers. The items
            table below carries the actual analytical weight; this line is just a
            quick scan of where volume concentrated this period.
            Empty-period: replaced with an inline "try a wider range" action that
            bumps the period selector to the next-larger granularity. */}
        {(() => {
          if (loading) {
            return (
              <div className="mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</div>
            );
          }
          if (!hasCurrentInvoices || catRollup.length === 0) {
            const wider = widerPeriod(period);
            return (
              <div className="mb-6 text-sm flex items-center gap-1.5 flex-wrap" style={{ color: 'var(--text-muted)' }}>
                <span>No purchases in this {periodLabel(period).toLowerCase()}.</span>
                {wider && (
                  <button
                    onClick={handleWiderRange}
                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-primary)', fontWeight: 500, textDecoration: 'underline' }}
                    title={`Switch to ${periodLabel(wider)} view`}
                  >
                    {`Try ${periodLabel(wider)} view \u2192`}
                  </button>
                )}
              </div>
            );
          }
          // Sort by current volume desc; render top 3 inline + remainder behind <details>.
          const sorted = catRollup.slice().sort((a, b) => b.curr - a.curr);
          const top = sorted.slice(0, 3);
          const rest = sorted.slice(3);
          const fmtCat = (b) => `${b.category} ${Math.round(b.curr).toLocaleString()} lbs`;
          const dot = ' \u00B7 ';
          return (
            <div className="mb-6 flex items-center gap-1 flex-wrap text-sm">
              <span style={{ color: 'var(--text-secondary)' }}>Top categories this period:</span>
              {top.map((b, i) => (
                <React.Fragment key={b.category}>
                  <button
                    onClick={() => setCategory(b.category)}
                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                    title={`Filter items table to ${b.category}`}
                  >
                    {fmtCat(b)}
                  </button>
                  {i < top.length - 1 && <span style={{ color: 'var(--text-muted)' }}>{dot}</span>}
                </React.Fragment>
              ))}
              {rest.length > 0 && (
                <>
                  <span style={{ color: 'var(--text-muted)' }}>{dot}</span>
                  <details className="inline">
                    <summary
                      className="cursor-pointer"
                      style={{ color: 'var(--text-secondary)', listStyle: 'none', textDecoration: 'underline' }}
                    >
                      {rest.length} more →
                    </summary>
                    <div className="mt-2 ml-1 flex flex-wrap items-center gap-1">
                      {rest.map((b, i) => (
                        <React.Fragment key={b.category}>
                          <button
                            onClick={() => setCategory(b.category)}
                            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                            title={`Filter items table to ${b.category}`}
                          >
                            {fmtCat(b)}
                          </button>
                          {i < rest.length - 1 && <span style={{ color: 'var(--text-muted)' }}>{dot}</span>}
                        </React.Fragment>
                      ))}
                    </div>
                  </details>
                </>
              )}
            </div>
          );
        })()}

        {/* First-period banner. Shown when current has activity but prior is
            entirely empty — typically a new company's first invoice month.
            Suppresses the per-row "New" chip noise and explains the state in
            one place. */}
        {isFirstPeriod && !loading && (
          <div
            className="mb-3 rounded-lg p-3 flex items-start gap-2"
            style={{ background: 'var(--bg-info-subtle)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginTop: 2, color: 'var(--text-info)' }} aria-hidden>
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div className="text-sm" style={{ lineHeight: 1.5 }}>
              <span style={{ fontWeight: 600 }}>First period of invoice history.</span>
              <span style={{ color: 'var(--text-secondary)' }}>{` Prior period (${fmtDateShort(priorRange.start)}\u2013${fmtDateShort(priorRange.end)}) has no invoices yet \u2014 prior comparison will populate next period.`}</span>
            </div>
          </div>
        )}

        {/* Items table — segmented by activity status. See DESIGN_SYSTEM.md
            §Activity-status segmentation. */}
        {(() => {
          if (loading) {
            return (
              <div className="rounded-lg p-10 text-center text-sm" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                {'Loading\u2026'}
              </div>
            );
          }

          const { active, skipped } = segmented;
          const hasFilter = category !== 'All' || vendorFilter !== '';
          const wider = widerPeriod(period);

          // Both groups empty — distinguish "filter zeroed everything" from
          // "the period itself has nothing usable."
          if (active.length === 0 && skipped.length === 0) {
            if (hasFilter) {
              return (
                <div className="rounded-lg p-10 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                  <div className="text-sm" style={{ color: 'var(--text-muted)' }}>No items match this filter in this period.</div>
                  <button
                    onClick={handleClearFilters}
                    className="mt-2 text-sm"
                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-primary)', fontWeight: 500, textDecoration: 'underline' }}
                  >
                    Clear filter
                  </button>
                </div>
              );
            }
            return (
              <div className="rounded-lg p-10 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="text-sm" style={{ color: 'var(--text-muted)' }}>No invoice activity in this period.</div>
                {wider && (
                  <button
                    onClick={handleWiderRange}
                    className="mt-2 text-sm"
                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-primary)', fontWeight: 500, textDecoration: 'underline' }}
                  >
                    {`Try ${periodLabel(wider)} view \u2192`}
                  </button>
                )}
              </div>
            );
          }

          return (
            <div className="space-y-4">
              {/* Active group — always rendered when we have any items at all. */}
              <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Active this period</span>
                  <span style={{
                    fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 9999,
                    background: 'var(--bg-subtle)', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums',
                  }}>{active.length}</span>
                </div>
                {active.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    {skipped.length > 0
                      ? 'All items in this period are listed in Skipped below.'
                      : 'No active items.'}
                  </div>
                ) : (
                  <ItemTable
                    rows={active}
                    variant="active"
                    activeAlertByItemId={activeAlertByItemId}
                    onRowClick={(r) => setDrawer(r)}
                    showNewChip={hasPriorInvoices}
                  />
                )}
              </div>

              {/* Skipped group — only renders if it has rows after filtering.
                  Default-collapsed via native <details>; resets on filter/period
                  change because the parent useMemo regenerates the array. */}
              {skipped.length > 0 && (
                <details className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                  <summary
                    className="px-4 py-3 flex items-center gap-2 cursor-pointer"
                    style={{ listStyle: 'none', color: 'var(--text-secondary)' }}
                  >
                    <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Skipped this period</span>
                    <span style={{
                      fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 9999,
                      background: 'var(--bg-subtle)', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums',
                    }}>{`${skipped.length} ${skipped.length === 1 ? 'item' : 'items'}`}</span>
                    <span className="ml-auto text-xs" style={{ color: 'var(--text-muted)' }}>
                      Items you ordered in the prior period but not this one
                    </span>
                  </summary>
                  <div style={{ borderTop: '1px solid var(--border)' }}>
                    <ItemTable
                      rows={skipped}
                      variant="skipped"
                      activeAlertByItemId={activeAlertByItemId}
                      onRowClick={(r) => setDrawer(r)}
                      showNewChip={hasPriorInvoices}
                    />
                  </div>
                </details>
              )}
            </div>
          );
        })()}
      </div>

      {/* Item detail drawer */}
      <SlideOverPanel
        open={drawer != null}
        onClose={() => setDrawer(null)}
        title={drawer?.name}
        subtitle={drawer && (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span>{drawer.category}</span>
            {drawer.is_catch_weight && <StatusPill tone="neutral">catch-weight</StatusPill>}
          </span>
        )}
      >
        {drawer && (
          <ItemDrawerBody
            item={drawer}
            range={range}
            priorRange={priorRange}
            companyId={companyId}
            alert={activeAlertByItemId[drawer.id]}
            onAlertChange={fetchAlerts}
          />
        )}
      </SlideOverPanel>
    </div>
  );
}

// ─── ItemTable ──────────────────────────────────────────────────────────────
// Renders a list of food_item roll-up rows with two variants:
//   variant="active"  — full column set including Δ Vol, sparkline, both $/lb
//                       columns. Top of the page.
//   variant="skipped" — compact, de-emphasized treatment. No Δ Vol column
//                       (the group label already says "skipped"). No
//                       sparkline. Greyed item names. "not ordered" muted
//                       label in place of the volume cell.
// Both variants preserve the drawer click target — operators can tap a
// skipped item to see what its prior-period activity looked like.
function ItemTable({ rows, variant, activeAlertByItemId, onRowClick, showNewChip = true }) {
  const isSkipped = variant === 'skipped';

  // Active grid: Item | Category | Volume | Δ Vol | $/lb | Prior $/lb | Δ $/lb | Sparkline
  // Skipped grid: Item | Category | Last volume | Status (was "Δ Vol") | $/lb | Prior $/lb | (gap)
  // Same gridTemplateColumns so visual rhythm matches across groups.
  const gridCols = '2fr 110px 110px 90px 90px 110px 90px 70px';
  const headers = isSkipped
    ? ['Item', 'Category', 'Last volume', '', '$/lb', 'Prior $/lb', '', '']
    : ['Item', 'Category', 'Volume', '\u0394 Vol', '$/lb', 'Prior $/lb', '\u0394 $/lb', ''];

  return (
    <div>
      {!isSkipped && (
        <div className="grid" style={{
          gridTemplateColumns: gridCols,
          background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)',
        }}>
          {headers.map((h, hi) => (
            <div key={h + hi} className="px-3 py-2" style={{
              fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em',
              color: 'var(--text-secondary)', textAlign: hi >= 2 && hi <= 6 ? 'right' : 'left',
            }}>{h}</div>
          ))}
        </div>
      )}
      {rows.map((r, i) => (
        <ItemRow
          key={r.id}
          row={r}
          isLast={i === rows.length - 1}
          variant={variant}
          alert={activeAlertByItemId[r.id]}
          onClick={() => onRowClick(r)}
          gridCols={gridCols}
          showNewChip={showNewChip}
        />
      ))}
    </div>
  );
}

function ItemRow({ row: r, isLast, variant, alert, onClick, gridCols, showNewChip = true }) {
  const isSkipped = variant === 'skipped';
  const unit = r.useWeight ? 'lbs' : 'cs';
  const volColor = r.volPct == null ? 'var(--text-muted)' : (r.volPct >= 0 ? 'var(--text-success)' : 'var(--text-critical)');
  const perLbColor = r.perLbPct == null ? 'var(--text-muted)' : (r.perLbPct > 0 ? 'var(--text-critical)' : 'var(--text-success)');
  // "New" applies when this item has current activity but no prior — but we
  // suppress the chip on the row when the ENTIRE prior window is empty
  // (showNewChip=false). In that "first period" case, the chip is redundant
  // with the page-level banner; surfacing it on every row is just noise.
  const isNew = !isSkipped && r.priorVol === 0 && r.currVol > 0;
  const renderNewChip = isNew && showNewChip;
  const nameColor = isSkipped || r.needs_review ? 'var(--text-muted)' : 'var(--text-primary)';

  return (
    <div
      className="grid cursor-pointer transition-colors"
      onClick={onClick}
      style={{
        gridTemplateColumns: gridCols,
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
        minHeight: isSkipped ? 44 : 52,
        opacity: isSkipped ? 0.85 : 1,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Item — name + chips */}
      <div className="px-3 py-2.5 flex items-center gap-2 min-w-0">
        <span
          className="text-sm font-medium truncate"
          style={{ color: nameColor }}
        >
          {r.name}
        </span>
        {renderNewChip && (
          <StatusPill tone="purple">New</StatusPill>
        )}
        {r.needs_review && (
          <span title="This item's pack/weight data looks suspicious. Open Data Review in Price Tracker to fix or dismiss.">
            <StatusPill tone="amber">
              <span className="inline-flex items-center gap-1">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                Needs review
              </span>
            </StatusPill>
          </span>
        )}
        {alert && (
          <span title={alert.signal_type === 'per_lb_spike'
            ? `$/lb up ${Math.abs(Number(alert.change_pct)).toFixed(1)}% \u2014 case price flat. Click for details.`
            : `Pack count dropped from ${Math.round(Number(alert.baseline_value))} to ${Math.round(Number(alert.current_value))}. Click for details.`}>
            <StatusPill tone="amber">
              <span className="inline-flex items-center gap-1">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                Shrinkflation
              </span>
            </StatusPill>
          </span>
        )}
        {r.is_catch_weight && <StatusPill tone="neutral">catch-weight</StatusPill>}
      </div>

      {/* Category */}
      <div className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{r.category || '\u2014'}</div>

      {/* Volume — for Skipped, this is the prior-period volume (the "last volume" we saw) */}
      <div className="px-3 py-2.5 text-sm font-semibold" style={{
        color: r.needs_review ? 'var(--text-muted)' : (isSkipped ? 'var(--text-secondary)' : 'var(--text-primary)'),
        textAlign: 'right', fontVariantNumeric: 'tabular-nums',
      }}>
        {r.needs_review ? (
          <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span>{'\u2014'}</span>
            <span className="italic" style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>(needs review)</span>
          </span>
        ) : isSkipped ? (
          r.priorVol > 0 ? `${Math.round(r.priorVol).toLocaleString()} ${unit}` : '\u2014'
        ) : (
          r.currVol > 0 ? `${Math.round(r.currVol).toLocaleString()} ${unit}` : '\u2014'
        )}
      </div>

      {/* Δ Vol — replaced with "not ordered" italic muted text on Skipped */}
      <div className="px-3 py-2.5 text-xs" style={{
        color: isSkipped ? 'var(--text-muted)' : volColor,
        textAlign: 'right',
        fontStyle: isSkipped ? 'italic' : 'normal',
      }}>
        {isSkipped ? 'not ordered' : (isNew ? '\u2014' : fmtDelta(r.volPct))}
      </div>

      {/* Current $/lb */}
      <div className="px-3 py-2.5 text-sm" style={{
        color: isSkipped ? 'var(--text-muted)' : 'var(--text-primary)',
        textAlign: 'right', fontVariantNumeric: 'tabular-nums',
      }}>
        {isSkipped ? '\u2014' : (r.currPerLb != null ? `$${r.currPerLb.toFixed(2)}` : '\u2014')}
      </div>

      {/* Prior $/lb */}
      <div className="px-3 py-2.5 text-sm" style={{ color: 'var(--text-secondary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {r.priorPerLb != null ? `$${r.priorPerLb.toFixed(2)}` : '\u2014'}
      </div>

      {/* Δ $/lb — blank on Skipped (no current value to compare) */}
      <div className="px-3 py-2.5 text-xs" style={{ color: isSkipped ? 'var(--text-muted)' : perLbColor, textAlign: 'right' }}>
        {isSkipped ? '' : fmtDelta(r.perLbPct)}
      </div>

      {/* Sparkline — current period only, omitted on Skipped (no current data to plot) */}
      <div className="px-3 py-2.5 flex justify-end">
        {!isSkipped && <Sparkline data={r.sparkline} tone="volume" width={60} height={20} />}
      </div>
    </div>
  );
}

// ─── Inventory detail drawer ────────────────────────────────────────────────
// Premium, insight-first single-chart drawer. Replaces the previous
// three-stacked-sparkline treatment which couldn't tell operators *why*
// something was changing.
//
// Layout (top → bottom):
//   1. Active shrinkflation alert card (only when present)
//   2. Headline insight block — 3 lines (spend summary, generated insight, date range)
//   3. Metric toggle (Volume | $/lb | Spend)
//   4. Single SVG line chart with gradient fill, period divider, hover tooltip
//   5. 2×2 context cards (Current $/lb, Pack, Vendor, Cadence)
//   6. Per-invoice history table — semantic color on $/lb deltas, prior-period rows muted
//   7. Italic disclosure footer
//   8. "View in Price Tracker →" link
//
// One round-trip data fetch on open, scoped to the combined window. Insight
// generation is delegated to lib/inventoryInsight.js so it stays a pure,
// testable function. See DESIGN_SYSTEM.md §Inventory detail drawer.

// ContextCard — exported so the Invoice Review drill-in can reuse the same
// 2×2 context-grid treatment as the Inventory Trends drawer. Single
// canonical implementation; do not fork.
export function ContextCard({ label, value, secondary, tone = null, valueClassName = '' }) {
  const valueColor =
    tone === 'critical' ? 'var(--text-critical)'
    : tone === 'success' ? 'var(--text-success)'
    : tone === 'warning' ? 'var(--text-warning)'
    : 'var(--text-primary)';
  return (
    <div
      className="rounded-lg p-3"
      style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
    >
      <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
        {label}
      </div>
      <div
        className={`text-base font-semibold mt-1 ${valueClassName}`}
        style={{ color: valueColor, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </div>
      <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
        {secondary}
      </div>
    </div>
  );
}

// PremiumLineChart + cardinalSplinePath + fmtDateShort lifted to
// src/components/PremiumLineChart.js so the Price Tracker volume drawer can
// share the same chart treatment. See that file for full implementation.

function ItemDrawerBody({ item, range, priorRange, companyId, alert, onAlertChange }) {
  const [detailLines, setDetailLines] = useState(null);
  const [showDismiss, setShowDismiss] = useState(false);
  const [metric, setMetric] = useState('volume');

  // One round-trip — invoice lines for the combined [prior.start, range.end]
  // window. Aliases dropped: vendor info now collapses into the ContextGrid.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('invoice_line_items')
        .select('id, invoice_id, invoice_date, vendor_name, quantity, unit, total_weight, price_per_lb, unit_price, line_total, pack_count, piece_weight')
        .eq('company_id', companyId)
        .eq('food_item_id', item.id)
        .gte('invoice_date', priorRange.start)
        .lte('invoice_date', range.end)
        .order('invoice_date', { ascending: true });
      if (cancelled) return;
      setDetailLines(data || []);
    })();
    return () => { cancelled = true; };
  }, [companyId, item.id, priorRange.start, range.end]);

  const lines = detailLines || [];
  const useWeight = hasWeightData(item, lines);

  const inCurrent = (l) => l.invoice_date >= range.start && l.invoice_date <= range.end;
  const inPrior = (l) => l.invoice_date >= priorRange.start && l.invoice_date <= priorRange.end;
  const currLines = useMemo(() => lines.filter(inCurrent), [lines, range.start, range.end]);
  const priorLines = useMemo(() => lines.filter(inPrior), [lines, priorRange.start, priorRange.end]);

  // Pure-function insight — testable, single source of truth for headline copy.
  const insight = useMemo(
    () => generateInsight({ item, currLines, priorLines, alert, useWeight }),
    [item, currLines, priorLines, alert, useWeight]
  );

  // Aggregates for context cards + headline spend line.
  const stats = useMemo(() => {
    const totalSpend = currLines.reduce((s, l) => s + (Number(l.line_total) || 0), 0);
    const orderIds = new Set(currLines.map(l => l.invoice_id).filter(v => v != null));
    const orders = orderIds.size || currLines.length;

    const perLbVals = currLines.map(l => Number(l.price_per_lb)).filter(v => v > 0);
    const avgPerLb = perLbVals.length ? perLbVals.reduce((a, b) => a + b, 0) / perLbVals.length : null;
    const priorPerLbVals = priorLines.map(l => Number(l.price_per_lb)).filter(v => v > 0);
    const priorAvgPerLb = priorPerLbVals.length ? priorPerLbVals.reduce((a, b) => a + b, 0) / priorPerLbVals.length : null;

    // Mirror inventoryInsight.js fmtPack — pack_count=1 collapses to piece_weight only.
    const fmtP = (l) => {
      if (l.pack_count == null) return null;
      const pc = Number(l.pack_count);
      const pw = l.piece_weight ? String(l.piece_weight).trim() : null;
      if (pc === 1 && pw) return pw;
      return pw ? `${pc}\u00D7${pw}` : `${pc}`;
    };
    const packs = currLines.map(fmtP).filter(Boolean);
    const distinctPacks = Array.from(new Set(packs));
    const latestPack = packs[packs.length - 1] || null;

    const vCounts = {};
    for (const l of currLines) {
      if (l.vendor_name) vCounts[l.vendor_name] = (vCounts[l.vendor_name] || 0) + 1;
    }
    const vSorted = Object.entries(vCounts).sort((a, b) => b[1] - a[1]);
    const topVendor = vSorted[0] ? vSorted[0][0] : null;
    const topVendorCount = vSorted[0] ? vSorted[0][1] : 0;
    const totalVendorMentions = currLines.filter(l => l.vendor_name).length;
    const topVendorShare = totalVendorMentions > 0 ? (topVendorCount / totalVendorMentions) * 100 : null;

    const dates = Array.from(new Set(currLines.map(l => l.invoice_date))).sort();
    let avgGap = null;
    if (dates.length >= 2) {
      const gaps = [];
      for (let i = 1; i < dates.length; i++) {
        const d0 = new Date(dates[i - 1] + 'T00:00:00').getTime();
        const d1 = new Date(dates[i] + 'T00:00:00').getTime();
        gaps.push((d1 - d0) / 86400000);
      }
      avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
    }
    return { totalSpend, orders, avgPerLb, priorAvgPerLb, distinctPacks, latestPack, topVendor, topVendorShare, avgGap, dates };
  }, [currLines, priorLines]);

  // Chart series — one point per invoice line, picks value by metric toggle.
  const chartSeries = useMemo(() => {
    return lines.map(l => {
      let value = null;
      if (metric === 'volume') value = useWeight ? Number(l.total_weight) || 0 : Number(l.quantity) || 0;
      else if (metric === 'perLb') value = l.price_per_lb != null ? Number(l.price_per_lb) : null;
      else if (metric === 'spend') value = Number(l.line_total) || 0;
      return { date: l.invoice_date, value, line: l };
    }).filter(p => p.value != null && isFinite(p.value));
  }, [lines, metric, useWeight]);

  const yFormat = (v) => {
    if (metric === 'volume') return useWeight ? `${Math.round(v)} lb` : `${v.toFixed(1)} cs`;
    if (metric === 'perLb') return `$${v.toFixed(2)}`;
    if (metric === 'spend') return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`;
    return String(v);
  };

  if (!detailLines) {
    return <div className="p-5 text-sm" style={{ color: 'var(--text-muted)' }}>{'Loading\u2026'}</div>;
  }

  const dateRangeLabel = `${fmtDateShort(range.start)} \u2013 ${fmtDateShort(range.end)}`;
  const fmtSpend = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(n).toLocaleString()}`;

  // Context-card derived values — handle the edge cases per spec: pieces-counted
  // items get a "Count" label, cadence with <3 invoices says "forming", vendor
  // share dilutes when split across multiple vendors.
  const packCardLabel = useWeight ? 'Pack' : 'Count';
  const packCardValue = stats.latestPack || '\u2014';
  const packCardSecondary = stats.distinctPacks.length > 1
    ? `${stats.distinctPacks.length} variants this period \u2014 check history`
    : (stats.latestPack ? (useWeight ? 'unchanged this period' : 'per case') : 'no pack data');

  const cadenceValue = stats.avgGap != null
    ? `~${stats.avgGap.toFixed(stats.avgGap < 10 ? 1 : 0)} days`
    : (stats.orders > 0 ? `${stats.orders} ${stats.orders === 1 ? 'order' : 'orders'}` : '\u2014');
  const cadenceSecondary = stats.dates.length < 3
    ? `Based on ${stats.dates.length} ${stats.dates.length === 1 ? 'order' : 'orders'} \u2014 forming`
    : `Across ${stats.dates.length} orders this period`;

  const vendorValue = stats.topVendor ? String(stats.topVendor).replace(/\n/g, ' \u00B7 ') : '\u2014';
  const vendorSecondary = stats.topVendorShare != null
    ? (stats.topVendorShare === 100 ? 'sole vendor this period' : `${stats.topVendorShare.toFixed(0)}% of orders`)
    : 'no vendor data';

  const perLbValue = stats.avgPerLb != null ? `$${stats.avgPerLb.toFixed(2)}` : '\u2014';
  const perLbDelta = (stats.avgPerLb != null && stats.priorAvgPerLb != null && stats.priorAvgPerLb > 0)
    ? ((stats.avgPerLb - stats.priorAvgPerLb) / stats.priorAvgPerLb) * 100
    : null;
  const perLbSecondary = perLbDelta == null
    ? (stats.avgPerLb != null ? 'avg this period' : 'no $/lb data')
    : (Math.abs(perLbDelta) < 0.5 ? 'flat vs prior' : `${perLbDelta > 0 ? '\u2191' : '\u2193'} ${Math.abs(perLbDelta).toFixed(0)}% vs prior`);
  const perLbDeltaTone = perLbDelta == null ? null : (perLbDelta > 0 ? 'critical' : 'success');

  return (
    <div className="p-5 space-y-5">
      {/* 1. Active shrinkflation alert (only when present). Same treatment as
          Vendor Watch active card for visual consistency. */}
      {alert && (
        <div
          className="rounded-lg p-4"
          style={{
            background: 'var(--bg-warning-subtle)',
            border: '1px solid var(--border)',
            borderLeft: '3px solid var(--text-warning)',
          }}
        >
          <div className="flex items-center gap-1.5 mb-1" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-warning)' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 9v4"/><path d="M12 17h.01"/><circle cx="12" cy="12" r="10"/></svg>
            Shrinkflation alert
          </div>
          <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
            {alert.signal_type === 'per_lb_spike'
              ? `$/lb up ${Math.abs(Number(alert.change_pct)).toFixed(1)}% over last 2 invoices \u00B7 case price flat`
              : `Pack count dropped from ${Math.round(Number(alert.baseline_value))} to ${Math.round(Number(alert.current_value))}`}
          </div>
          <div className="flex justify-end mt-2">
            <button
              onClick={() => setShowDismiss(true)}
              className="text-xs px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 500 }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
            >
              Dismiss alert
            </button>
          </div>
        </div>
      )}
      {showDismiss && alert && (
        <DismissAlertModal
          alert={alert}
          onClose={() => setShowDismiss(false)}
          onDismissed={() => { setShowDismiss(false); onAlertChange?.(); }}
        />
      )}

      {/* 2. Headline insight — 3 lines: spend summary, generated insight, date range */}
      <div className="space-y-1">
        <div className="text-base font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
          {fmtSpend(stats.totalSpend)} this period across {stats.orders} {stats.orders === 1 ? 'order' : 'orders'}
        </div>
        <div className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {insight.headline}
        </div>
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {dateRangeLabel}
        </div>
      </div>

      {/* 3. Metric toggle */}
      <div>
        <SegmentedPills
          value={metric}
          onChange={setMetric}
          options={[
            { key: 'volume', label: useWeight ? 'Volume (lbs)' : 'Volume (cases)' },
            { key: 'perLb', label: '$/lb' },
            { key: 'spend', label: 'Spend' },
          ]}
          size="sm"
        />
      </div>

      {/* 4. Trend chart */}
      <PremiumLineChart
        series={chartSeries}
        range={range}
        priorRange={priorRange}
        yFormat={yFormat}
      />

      {/* 5. 2×2 context cards */}
      <div className="grid grid-cols-2 gap-3">
        <ContextCard
          label="Current $/lb"
          value={perLbValue}
          secondary={perLbSecondary}
          tone={perLbDeltaTone}
        />
        <ContextCard
          label={packCardLabel}
          value={packCardValue}
          secondary={packCardSecondary}
          tone={stats.distinctPacks.length > 1 ? 'warning' : null}
        />
        <ContextCard
          label="Vendor"
          value={vendorValue}
          secondary={vendorSecondary}
          valueClassName="text-sm"
        />
        <ContextCard
          label="Cadence"
          value={cadenceValue}
          secondary={cadenceSecondary}
        />
      </div>

      {/* 6. Per-invoice history — semantic color on $/lb deltas vs previous row,
          prior-period rows get a muted background + 70% opacity. */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
          Per-invoice history
        </div>
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          <div className="grid text-xs" style={{
            gridTemplateColumns: '88px 1.4fr 56px 70px 72px 90px',
            background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)',
          }}>
            {[
              { label: 'Date', align: 'left' },
              { label: 'Vendor', align: 'left' },
              { label: 'Qty', align: 'right' },
              { label: '$/cs', align: 'right' },
              { label: '$/lb', align: 'right' },
              { label: 'Pack', align: 'left' },
            ].map(h => (
              <div key={h.label} className="px-3 py-2"
                style={{
                  color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                  textAlign: h.align,
                }}
              >{h.label}</div>
            ))}
          </div>
          {lines.length === 0 ? (
            <div className="px-3 py-4 text-xs text-center" style={{ color: 'var(--text-muted)' }}>No invoices in this window.</div>
          ) : (
            lines.map((l, i) => {
              const prev = i > 0 ? lines[i - 1] : null;
              const perLb = l.price_per_lb != null ? Number(l.price_per_lb) : null;
              const prevPerLb = prev?.price_per_lb != null ? Number(prev.price_per_lb) : null;
              const perLbChanged = perLb != null && prevPerLb != null && Math.abs(perLb - prevPerLb) / prevPerLb >= 0.02;
              const perLbColor = !perLbChanged
                ? 'var(--text-primary)'
                : (perLb > prevPerLb ? 'var(--text-critical)' : 'var(--text-success)');
              const isCurrent = inCurrent(l);
              return (
                <div key={i} className="grid text-xs" style={{
                  gridTemplateColumns: '88px 1.4fr 56px 70px 72px 90px',
                  borderBottom: i < lines.length - 1 ? '1px solid var(--border)' : 'none',
                  background: isCurrent ? 'transparent' : 'var(--bg-subtle)',
                  opacity: isCurrent ? 1 : 0.7,
                }}>
                  <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtDateShort(l.invoice_date)}
                  </div>
                  <div className="px-3 py-2 truncate" style={{ color: 'var(--text-secondary)' }} title={l.vendor_name}>
                    {(l.vendor_name || '\u2014').replace(/\n/g, ' \u00B7 ')}
                  </div>
                  <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                    {Number(l.quantity || 0)}
                  </div>
                  <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                    {l.unit_price != null ? `$${Number(l.unit_price).toFixed(2)}` : '\u2014'}
                  </div>
                  <div className="px-3 py-2" style={{ color: perLbColor, fontVariantNumeric: 'tabular-nums', fontWeight: perLbChanged ? 600 : 400, textAlign: 'right' }}>
                    {perLb != null ? `$${perLb.toFixed(2)}` : '\u2014'}
                  </div>
                  <div className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>
                    {l.piece_weight && Number(l.pack_count) === 1
                      ? l.piece_weight
                      : (l.pack_count && l.piece_weight ? `${l.pack_count}\u00D7${l.piece_weight}` : (l.pack_count ? String(l.pack_count) : '\u2014'))}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {priorLines.length > 0 && (
          <div className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
            {`Greyed rows = prior period (${fmtDateShort(priorRange.start)}\u2013${fmtDateShort(priorRange.end)}) for context`}
          </div>
        )}
      </div>

      {/* 7. Disclosure footer — shared with Volume detail drawer (Price Tracker). */}
      <div className="pt-1 italic" style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {'Posted sees your invoices, not your sales. If you know why this changed \u2014 new menu item, event, supplier swap \u2014 that context is yours to add.'}
      </div>

      {/* 8. View in Price Tracker — Price Tracker now lives under the
          Inventory top-level group, not Operations. */}
      <div>
        <SecondaryBtn
          onClick={() => navigate('inventory', 'pricetracker')}
          className="!text-sm"
        >
          {`View in Price Tracker \u2192`}
        </SecondaryBtn>
      </div>
    </div>
  );
}
